import { Fragment } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import { isAuthenticated } from '~/auth/slices/auth.slice'
import ROUTES from '~/shared/constants/routes.const'
import { PAYWALL_TRIGGER_ATTRIBUTE } from '~/subscription/hooks/useDeferredPaywallTrigger'
import { AppLanguage } from '~/types/locale'
import { Link } from '~/components/Link'
import { selectIsSubscriptionApplicable } from '~/redux/slices/subscription.slice'
import { useAppSelector } from '~/redux/hooks'
import CurriculumSelection from '~/curriculum/components/CurriculumSelection'
import { CurriculumSelectionPoints } from '~/curriculum/constants/curriculum.const'
import DownloadAppBadge from '~/shared/components/DownloadAppBadge'
import appConfig from '~/config/app'
import useCurrentFamily from '~/familyAccount/hooks/useCurrentFamily'
import useSubscriptionToggle from '~/subscription/hooks/useSubscriptionToggle'
import TermsAndConditions from '~/components/Footer/TermsAndConditions'

import { IconName } from './Icons'
import SVGImage from './Image/SVGImage'
import { ExternalLink } from './ExternalLink'
import HomeThinIcon from './Icons/svgs/HomeThinIcon'
import UserGroupThinIcon from './Icons/svgs/UserGroupThinIcon'
import BriefcaseThinIcon from './Icons/svgs/BriefcaseThinIcon'
import CameraThinIcon from './Icons/svgs/CameraThinIcon'
import LecturesIcon from './Icons/svgs/LecturesIcon'
import PhoneThinIcon from './Icons/svgs/PhoneThinIcon'
import LocationPinThinIcon from './Icons/svgs/LocationPinThinIcon'
import EnvelopeThinIcon from './Icons/svgs/EnvelopeThinIcon'
import BlogIcon from './Icons/svgs/BlogIcon'
import SVGIcon from './Icons/svgs/SVGIcon'
import LinkedinIcon from './Icons/svgs/LinkedinIcon'
import FacebookIcon from './Icons/svgs/FacebookIcon'
import InstagramIcon from './Icons/svgs/InstagramIcon'
import QuestionCircleIcon from './Icons/svgs/QuestionCircleIcon'
import BookTutorButton from './BookTutorButton'

export function Footer({
  hideHorizontalMenu = false,
  hideCurriculumSelection,
  pricingPath,
}: {
  hideHorizontalMenu?: boolean
  hideCurriculumSelection?: boolean
  /** Replaces the price page link for every visitor, e.g. to carry a campaign partner. */
  pricingPath?: string
}): React.ReactElement {
  const { t } = useTranslation('footer')
  const locale = useRouter().locale as AppLanguage
  const isNO = locale === AppLanguage.NO

  return (
    <footer
      {...{ [PAYWALL_TRIGGER_ATTRIBUTE]: 'true' }}
      className={`relative w-full overflow-hidden pt-12 bg-purple-gdark ${
        hideHorizontalMenu ? 'pb-28' : 'pb-32 lg:pb-16'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <section className="flex flex-col lg:flex-row lg:justify-between text-white">
          <About />
          <Contact isNO={isNO} />
          <Links pricingPath={pricingPath} />
          {isNO && !appConfig.isMobileApp && !appConfig.isSchoolPlatform && (
            <div className="flex justify-center lg:justify-start lg:items-end mb-8 lg:mb-5.5 -mt-4 lg:mt-0">
              <BookTutorButton />
            </div>
          )}
          {!hideCurriculumSelection && !appConfig.isSchoolPlatform && (
            <div className="lg:hidden">
              <CurriculumSelection selectionPoint={CurriculumSelectionPoints.FOOTER} />
            </div>
          )}
        </section>
        <hr className="mt-2 mb-14 border-gray-extra-light opacity-60" />

        <section className="flex flex-col lg:flex-row items-center justify-between space-y-8 lg:space-y-0 text-white">
          <FooterLogo />
          <TermsAndConditions />
          <Social />
        </section>
        {/* Gated here as well as inside CurriculumSelection, or the empty wrapper leaves an mt-8 gap. */}
        {!hideCurriculumSelection && !appConfig.isSchoolPlatform && (
          <div className="hidden lg:flex justify-center mt-8">
            <CurriculumSelection hideTitle selectionPoint={CurriculumSelectionPoints.FOOTER} />
          </div>
        )}
      </div>
      {!appConfig.isMobileApp && (
        <div className="flex flex-col justify-center items-center gap-4 mx-auto mt-8">
          <h3 className="text-white text-medium font-semibold tracking-btn-tight">{t('downloadApp')}</h3>
          <DownloadAppBadge width={165} />
        </div>
      )}
    </footer>
  )
}

function About(): React.ReactElement {
  const { t } = useTranslation('footer')

  return (
    <div>
      <div className="mb-4 font-medium">{t('about')}</div>
      <ul className="mb-12 space-y-4">
        <Item icon="home-thin" label={t('aboutHoM')} to={ROUTES.ABOUT} />
        <Item icon="user-group-thin" label={t('aboutTeam')} to={ROUTES.OUR_TEAM} />
        <Item href="https://www.finn.no/job/employer/company/7156" icon="briefcase-thin" label={t('career')} />
        <Item icon="camera-thin" label={t('media')} to={ROUTES.MEDIA_NEWS} />
        <Item icon="lectures" label={t('lectures')} to={ROUTES.LECTURES} />
        {!appConfig.isMobileApp && !appConfig.isSchoolPlatform && (
          <Item icon="hom-blog" label={t('blog')} to={ROUTES.TUTOR_ON_DEMAND_BLOG} />
        )}
      </ul>
    </div>
  )
}

function Contact({ isNO }: { isNO: boolean }): React.ReactElement {
  const { t } = useTranslation('footer')

  return (
    <div>
      <div className="mb-4 font-semibold">{t('contact')}</div>

      <p className="flex items-center space-x-4 mb-5">
        <ExternalLink className="text-white opacity-75 underline" href="mailto:support@houseofmath.com">
          support@houseofmath.com
        </ExternalLink>
      </p>
      {isNO && (
        <>
          <p className="mb-4 font-semibold max-w-70">{t('bookingOfPrivateTutoring')}</p>
          <p className="mb-5 space-y-4 flex items-center space-x-4 text-white opacity-75">+47 22 150 300</p>
        </>
      )}
      <p className="mb-4 font-semibold">{t('address')}</p>
      <p className="mb-5 space-y-4 text-white opacity-75 min-w-30 w-full">
        {isNO ? (
          <>
            Bygdøy allé 23
            <br />
            0262 Oslo
            <br />
            {t('norway')}
          </>
        ) : (
          <>
            House of Math
            <br />
            470 Ramona Street
            <br />
            Palo Alto, CA 94301
            <br />
            USA
          </>
        )}
      </p>
      <ul className="mb-12 space-y-4">
        <Item icon="question-circle" label="FAQ" to={ROUTES.FAQ} />
      </ul>
    </div>
  )
}

const getIconComponent = (name: string): ((props: { [key: string]: any }) => React.ReactElement) => {
  switch (name) {
    case 'home-thin':
      return HomeThinIcon
    case 'user-group-thin':
      return UserGroupThinIcon
    case 'briefcase-thin':
      return BriefcaseThinIcon
    case 'camera-thin':
      return CameraThinIcon
    case 'lectures':
      return LecturesIcon
    case 'hom-blog':
      return BlogIcon
    case 'envelope-thin':
      return EnvelopeThinIcon
    case 'question-circle':
      return QuestionCircleIcon
    case 'phone-thin':
      return PhoneThinIcon
    case 'location-pin-thin':
      return LocationPinThinIcon
    case 'linked-in':
      return LinkedinIcon
    case 'facebook':
      return FacebookIcon
    case 'instagram':
      return InstagramIcon
    default:
      return HomeThinIcon
  }
}

function Item({
  href,
  icon,
  isEmail,
  label,
  to,
}: {
  href?: string
  icon: IconName
  isEmail?: boolean
  label: string
  to?: string
}): React.ReactElement {
  function Content(): React.ReactElement {
    return (
      <Fragment>
        <div className="self-start">
          <SVGIcon icon={getIconComponent(icon)} pathClassName="currentColor" />
        </div>
        <div className="whitespace-pre-line font-light text-white opacity-75 max-w-50">{label}</div>
      </Fragment>
    )
  }

  const className = 'flex items-center space-x-4 text-gray-extra-light'

  if (to) {
    return (
      <li>
        <Link className={className} to={to}>
          <Content />
        </Link>
      </li>
    )
  } else if (href) {
    return (
      <li>
        <a className={className} href={href}>
          <Content />
        </a>
      </li>
    )
  } else if (isEmail) {
    return (
      <li>
        <a className={className} href={`mailto: ${label}`}>
          <Content />
        </a>
      </li>
    )
  } else {
    return (
      <li className={className}>
        <Content />
      </li>
    )
  }
}

function Links({ pricingPath: overridePricingPath }: { pricingPath?: string }): React.ReactElement {
  const { isFamilyAdmin, isFamilyMember } = useCurrentFamily()
  const { updateSubscriptionPackageType } = useSubscriptionToggle()
  const { t } = useTranslation('footer')
  const router = useRouter()
  const language = router.locale as AppLanguage
  const isSubscriptionApplicable = useAppSelector(selectIsSubscriptionApplicable)
  const authenticated = useAppSelector(isAuthenticated)
  // A partner page sends signed-in visitors to the partner price page too, which handles customers and keeps the coupon.
  const pricingPath = overridePricingPath ?? (authenticated ? ROUTES.SUBSCRIPTION_PAGE : ROUTES.GET_STARTED_PRICE)

  const onPriceClick = (): void => {
    if (isFamilyMember || isFamilyAdmin) {
      updateSubscriptionPackageType('family')
    }
  }

  return (
    <div>
      <div className="mb-4 font-medium">{t('links')}</div>
      <ul className="mb-12 text-white text-opacity-75 font-light space-y-4 pl-1">
        <li>
          <ExternalLink className="" href={`https://vitaransvar.${language === 'no' ? 'no' : 'com'}`}>
            {t('weTakeAction')}
          </ExternalLink>
        </li>
        {!appConfig.isSchoolPlatform && (
          <li>
            <Link to={ROUTES.MATHMAGIC}>{t('mathMagic')}</Link>
          </li>
        )}
        {!appConfig.isMobileApp && !appConfig.isSchoolPlatform && (
          <li>
            <Link to={ROUTES.TUTOR_ON_DEMAND}>{t('tutoring')}</Link>
          </li>
        )}
        <li>
          <Link to={ROUTES.MATH_VAULT}>{t('mathVault')}</Link>
        </li>
        <li>
          <Link to={ROUTES.DRILL}>{t(`penNPaperExercise`)}</Link>
        </li>
        <li>
          <Link to={ROUTES.ENCYCLOPEDIA}>{t('encyclopedia')}</Link>
        </li>
        {isSubscriptionApplicable && (
          <li className="bg-orange rounded-1 w-fit px-1 text-black">
            <Link onClick={onPriceClick} rel="nofollow" to={pricingPath}>
              {t('pricing')}
            </Link>
          </li>
        )}
      </ul>
    </div>
  )
}

function FooterLogo(): React.ReactElement {
  return (
    <Link to={ROUTES.HOME}>
      <SVGImage alt="logo" height={38} loading="lazy" srcKey="logos/house-of-math-white.svg" width={193} />
    </Link>
  )
}

function Social(): React.ReactElement {
  return (
    <div className="flex items-center space-x-4">
      <SocialItem ariaLabel="Linkedin" icon="linked-in" url="https://www.linkedin.com/company/house-of-math/" />
      <SocialItem ariaLabel="Facebook" icon="facebook" url="https://www.facebook.com/houseofmath.as" />
      <SocialItem ariaLabel="Instagram" icon="instagram" url="https://www.instagram.com/houseofmath/" />
    </div>
  )
}

function SocialItem({ icon, url, ariaLabel }: { icon: IconName; url: string; ariaLabel: string }): React.ReactElement {
  return (
    <ExternalLink className="text-center" href={url}>
      <div className="p-3 rounded-full border border-white/75 opacity-90 hover:opacity-100">
        <SVGIcon aria-label={ariaLabel} icon={getIconComponent(icon)} pathClassName="text-white" size={18} />
      </div>
    </ExternalLink>
  )
}
